import { Card, CardContent } from '@components/ui';
import type { AIPurchaseStateBreakdownItem } from '../../../types/api.types';
import { formatCount } from './aiPurchaseAnalyticsUtils';
import PercentRow from './PercentRow';

interface StateBreakdownGridProps {
    states: AIPurchaseStateBreakdownItem[];
    loading: boolean;
    error: string | null;
    onSelectState: (state: string) => void;
}

/**
 * Per-state accent colors — each state gets its own distinct hue (dot, CTA text,
 * left border) on an otherwise neutral white card, so the 5 state cards are visually
 * distinguishable from each other, from the Overall summary card above, and from the
 * bright teal Dashboard homepage entry tile (which is intentionally left untouched).
 * Falls back to a neutral slate accent for any state not in this map.
 */
const STATE_ACCENTS: Record<string, { border: string; dot: string; cta: string }> = {
    'Uttar Pradesh': { border: 'border-l-[#6366f1]', dot: 'bg-[#6366f1]', cta: 'text-[#4f46e5]' },
    Assam: { border: 'border-l-[#f59e0b]', dot: 'bg-[#f59e0b]', cta: 'text-[#b45309]' },
    Maharashtra: { border: 'border-l-[#0ea5e9]', dot: 'bg-[#0ea5e9]', cta: 'text-[#0369a1]' },
    Odisha: { border: 'border-l-[#10b981]', dot: 'bg-[#10b981]', cta: 'text-[#047857]' },
    Rajasthan: { border: 'border-l-[#f43f5e]', dot: 'bg-[#f43f5e]', cta: 'text-[#be123c]' },
};
const DEFAULT_ACCENT = { border: 'border-l-[#94a3b8]', dot: 'bg-[#94a3b8]', cta: 'text-[#475569]' };

function getStateAccent(state: string) {
    return STATE_ACCENTS[state] ?? DEFAULT_ACCENT;
}

/**
 * Per-state AI purchase adoption breakdown (all 5 AI-enabled states). Each tile is
 * a neutral white card (matches this app's standard `Card` look) with its own accent
 * colour, dark legible text, and the standard red/amber/green adoption badges — and
 * navigates to the brand breakdown page for that state (real route change, not an
 * in-page state toggle — see the page component for the `navigate()` wiring).
 */
export default function StateBreakdownGrid({
    states,
    loading,
    error,
    onSelectState,
}: StateBreakdownGridProps) {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-[#6e64f0] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return <p className="text-sm text-[#dc2626] text-center py-6">{error}</p>;
    }

    if (states.length === 0) {
        return (
            <p className="text-sm text-[#64748b] text-center py-6">
                No state-level AI purchase data available for this date range.
            </p>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {states.map((item) => {
                const accent = getStateAccent(item.state);

                return (
                    <Card
                        key={item.state}
                        hover
                        onClick={() => onSelectState(item.state)}
                        role="button"
                        className={`cursor-pointer border-l-4 ${accent.border}`}
                    >
                        <CardContent className="py-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <span className={`w-2.5 h-2.5 rounded-full ${accent.dot}`} />
                                    <h3 className="text-base font-semibold text-[#1e293b]">
                                        {item.state}
                                    </h3>
                                </div>
                                <span className="text-xs text-[#64748b]">
                                    {formatCount(item.totalActivePurchases)} purchases
                                </span>
                            </div>

                            <div className="space-y-3">
                                <PercentRow
                                    label="By purchases"
                                    percent={item.adoptionPercentByPurchases}
                                    denominator={item.totalActivePurchases}
                                    linkedCount={item.aiLinkedPurchases}
                                    totalCount={item.totalActivePurchases}
                                />
                                <PercentRow
                                    label="By line items"
                                    percent={item.adoptionPercentByLineItems}
                                    denominator={item.totalLineItems}
                                    linkedCount={item.aiLinkedLineItems}
                                    totalCount={item.totalLineItems}
                                />
                            </div>

                            <p className={`mt-4 text-xs font-semibold ${accent.cta}`}>
                                View brands in {item.state} →
                            </p>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}
